import {FormControl} from '@primer/react-brand/esm'
import {useCallback, useMemo, useRef, useState} from 'react'
import {parsePhoneNumberFromString} from 'libphonenumber-js'
import type {CountryCode} from 'libphonenumber-js'
import type {Country} from '../../../../forms/Form/components/ConsentExperience/types'
import {DEFAULT_COUNTRY, buildCountryOptions, getPhoneSubmissionDetails} from './phone-utils'
import {CountrySearchableSelector} from './CountrySearchableSelector'
import styles from './PhoneInputWithCountrySearch.module.css'

export type PhoneInputWithCountrySearchProps = {
  id: string
  validationErrorId: string
  label: string
  error?: string
  required?: boolean
  placeholder?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  name?: string
  allowedCountries?: Country[]
}

// Custom hook for managing hidden input value
function useHiddenInput() {
  const hiddenInputRef = useRef<HTMLInputElement>(null)

  const updateValue = useCallback((value: string) => {
    if (hiddenInputRef.current) {
      const valueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
      valueSetter?.call(hiddenInputRef.current, value)
      // Keep the hidden field wired into the form state updates after imperatively syncing its value.
      hiddenInputRef.current.dispatchEvent(new Event('input', {bubbles: true}))
    }
  }, [])

  return {hiddenInputRef, updateValue}
}

export function PhoneInputWithCountrySearch(props: PhoneInputWithCountrySearchProps) {
  const {id, label, error, required, placeholder, validationErrorId, onChange, allowedCountries, name} = props

  const countries = useMemo(() => buildCountryOptions(allowedCountries), [allowedCountries])
  const defaultCountry = useMemo(
    () => countries.find(country => country.code === DEFAULT_COUNTRY)?.code ?? countries[0]?.code ?? DEFAULT_COUNTRY,
    [countries],
  )
  const supportedCountryCodes = useMemo(() => new Set(countries.map(country => country.code)), [countries])

  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(defaultCountry)
  const [phoneNumber, setPhoneNumber] = useState('')

  const {hiddenInputRef, updateValue} = useHiddenInput()

  const phoneInputRef = useRef<HTMLInputElement>(null)
  const hasError = typeof error === 'string'

  const handleCountrySelect = useCallback(
    (code: CountryCode) => {
      setSelectedCountry(code)
      setPhoneNumber('')
      updateValue('')
    },
    [updateValue],
  )

  const handlePhoneNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawInput = e.target.value
      const {formattedPhone, detectedCountry, displayInput} = getPhoneSubmissionDetails(
        rawInput,
        selectedCountry,
        parsePhoneNumberFromString,
      )

      // When a pasted international number (`+44…`) parses cleanly, swap the visible
      // value to the national form so the country selector and the input don't both
      // show the country code.
      setPhoneNumber(displayInput ?? rawInput)

      if (detectedCountry && supportedCountryCodes.has(detectedCountry) && detectedCountry !== selectedCountry) {
        setSelectedCountry(detectedCountry)
      }

      updateValue(formattedPhone)
    },
    [selectedCountry, supportedCountryCodes, updateValue],
  )

  return (
    <FormControl key={id} fullWidth required={required} validationStatus={hasError ? 'error' : undefined}>
      <>
        <FormControl.Label htmlFor={id} required={required} validationStatus={hasError ? 'error' : undefined}>
          {label}
        </FormControl.Label>

        <div className={styles.container}>
          <div className={styles.inputWrapper} data-error={hasError}>
            <div className={styles.countrySelector}>
              <CountrySearchableSelector
                id={id}
                countries={countries}
                selectedCountry={selectedCountry}
                onSelect={handleCountrySelect}
                nextFocusRef={phoneInputRef}
              />
            </div>

            <input
              id={id}
              ref={phoneInputRef}
              type="tel"
              autoComplete="tel-national"
              className={styles.phoneInput}
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              placeholder={placeholder}
              aria-describedby={hasError ? validationErrorId : undefined}
              aria-label={label}
              aria-invalid={hasError}
              aria-required={required}
            />
          </div>
        </div>

        <input
          name={name}
          className={styles.visuallyHidden}
          onChange={onChange}
          ref={hiddenInputRef}
          tabIndex={-1}
          aria-hidden="true"
        />

        {hasError ? (
          <FormControl.Validation id={validationErrorId} validationStatus="error">
            {error}
          </FormControl.Validation>
        ) : null}
      </>
    </FormControl>
  )
}
